import { IconX } from '@tabler/icons-react';
import { useRef, useState, useEffect, useCallback } from 'react';
import { getAuthorizationHeader, http } from '../../config/http';
import CetakStroke from './CetakStroke';
import { toast } from 'react-toastify';
import soundSuccess from '../../assets/sound.mp3';
import { CurrencyInput } from '../../utils/CurrencyInput';
import TestingPrint from './TestingPrint';
import PrintSuratTrxPenjualan from '../Transaksi/PrintSuratTrxPenjualan';
import SuratKeruak from '../Transaksi/SuratKeruak';
import SuratToko from '../../components/surat/SuratToko';
import { PhotoView, PhotoProvider } from 'react-photo-view';
import { imageBaseUrl } from '../../config/http';
import { useMasterData } from '../../context/MasterDataContext';
import ModalKonfirmasiPenjualan from './ModalKonfirmasiPenjualan';
import { terbilang } from '../../utils/number';
import BankLogo from '../../components/BankLogo';

export default function DetailBarangPenjualan({ itemCabang, detailPenjualan, isTertunda, loading, kodeItem, onReset }) {
  const { bankList: masterBankList } = useMasterData();
  const bankList = masterBankList && masterBankList.length > 0 ? masterBankList : ['BCA', 'BNI', 'BRI', 'Mandiri', 'BSI', 'Lainnya'];
  const defaultBank = bankList[0] || 'BCA';

  const activeItem = Array.isArray(itemCabang)
    ? (itemCabang.length > 0 ? itemCabang[0] : null)
    : (itemCabang || null);
  const item_id = activeItem?.id;
  const hasValidItem = Boolean(activeItem?.id || activeItem?.kode_item);

  const getEffectiveBankList = (currentBank) => {
    if (currentBank && !bankList.includes(currentBank)) {
      return [currentBank, ...bankList];
    }
    return bankList;
  };

  const [hargaPenjualan, setHarga] = useState('');
  const [msg] = useState('');
  const [btnLoading, setBtnLoading] = useState(false);
  const [harga_jual, setHargaJual] = useState('');
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  // Mode Pembayaran: 'single' (1 Metode Penuh 100%) vs 'split' (Bagi Pembayaran)
  const [paymentMode, setPaymentMode] = useState('single');
  const [singleMethod, setSingleMethod] = useState('cash'); // 'cash' | 'qris' | 'transfer' | 'edc'
  const [singleBank, setSingleBank] = useState(defaultBank);

  const [splitMethods, setSplitMethods] = useState([
    { id: 'cash', label: 'Cash', nama_bank: '', nominal: '' },
    { id: 'transfer', label: 'Transfer', nama_bank: defaultBank, nominal: '' },
    { id: 'qris', label: 'QRIS', nama_bank: '', nominal: '' },
    { id: 'edc', label: 'EDC', nama_bank: defaultBank, nominal: '' },
  ]);

  const [selectImage, setSelectImage] = useState([]);
  const [gambar, setGambar] = useState('');
  const [namaPembeli, setNamaPembeli] = useState('');
  const [noHp, setNoHp] = useState('');

  // Reset semua inputan ke default empty
  const resetAllInputs = useCallback(() => {
    setHarga('');
    setHargaJual('');
    setNamaPembeli('');
    setNoHp('');
    setPaymentMode('single');
    setSingleMethod('cash');
    setSingleBank(defaultBank);
    setSplitMethods([
      { id: 'cash', label: 'Cash', nama_bank: '', nominal: '' },
      { id: 'transfer', label: 'Transfer', nama_bank: defaultBank, nominal: '' },
      { id: 'qris', label: 'QRIS', nama_bank: '', nominal: '' },
      { id: 'edc', label: 'EDC', nama_bank: defaultBank, nominal: '' },
    ]);
    setSelectImage([]);
    setGambar('');
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) fileInput.value = '';
  }, [defaultBank]);

  // Sinkronisasi bank default jika master data bank baru selesai dimuat
  useEffect(() => {
    if (defaultBank) {
      setSingleBank((prev) => prev || defaultBank);
      setSplitMethods((prev) =>
        prev.map((m) => {
          if ((m.id === 'transfer' || m.id === 'edc') && !m.nama_bank) {
            return { ...m, nama_bank: defaultBank };
          }
          return m;
        })
      );
    }
  }, [defaultBank]);

  const currentItemId = activeItem?.id;
  const prevItemIdRef = useRef(currentItemId);

  // Reset semua inputan ketika kodeItem di-reset/dihapus atau berganti item
  useEffect(() => {
    if (!kodeItem || !kodeItem.trim() || !hasValidItem) {
      resetAllInputs();
    } else if (prevItemIdRef.current && prevItemIdRef.current !== currentItemId) {
      resetAllInputs();
    }
    prevItemIdRef.current = currentItemId;
  }, [kodeItem, hasValidItem, currentItemId, resetAllInputs]);

  const [dataPrint, setDataPrint] = useState([]);
  let [isOpen, setIsOpen] = useState(false);

  const hargaTotalRef = useRef(null);

  useEffect(() => {
    // Ketika pencarian item ditemukan di halaman kasir, otomatis fokus kursor ke input harga total
    if (hasValidItem) {
      const timer = setTimeout(() => {
        const inputEl = hargaTotalRef.current || document.getElementById('input-harga-total');
        if (inputEl) {
          inputEl.focus();
          inputEl.select?.();
        }
      }, 80);
      return () => clearTimeout(timer);
    }
  }, [hasValidItem, currentItemId]);

  function playSound() {
    new Audio(soundSuccess).play();
  }

  const handleChange = (newValue) => {
    let val = newValue === undefined ? '' : newValue;
    setHarga(val);
    setHargaJual(val);

    // Jika sedang dalam mode split, otomatis sesuaikan nominal cash dengan harga jual baru
    if (paymentMode === 'split') {
      const newPrice = parseInt(val) || 0;
      setSplitMethods((prev) => {
        const nonCashTotal = prev.reduce((sum, p) => {
          return p.id !== 'cash' ? sum + (parseInt(p.nominal) || 0) : sum;
        }, 0);
        const remainingForCash = newPrice - nonCashTotal;
        const newCash = remainingForCash > 0 ? remainingForCash : (nonCashTotal > 0 ? 0 : newPrice);

        return prev.map((p) => {
          if (p.id === 'cash') {
            return {
              ...p,
              nominal: newCash > 0 ? newCash : (newPrice > 0 && nonCashTotal >= newPrice ? 0 : '')
            };
          }
          return p;
        });
      });
    }
  };

  function numberWithCommas(x) {
    if (x === null || x === undefined || x === '') return '0';
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function checkBerat(number) {
    if (number === null || number === undefined || number === '') return '0';
    const beratConfirm = parseFloat(number);
    return beratConfirm % 1 === 0
      ? beratConfirm.toString()
      : beratConfirm
          .toFixed(2)
          .replace(/\.?0+$/, '')
          .replace('.', ',');
  }

  const strukComponent = useRef();

  function closeModal() {
    setIsOpen(false);
    setDataPrint([]);
    resetAllInputs();
    if (onReset) {
      onReset();
    }
  }

  // Beralih ke Mode Split - Default otomatis terisi: Cash = total bayarnya
  const switchToSplit = useCallback(() => {
    const currentPrice = parseInt(harga_jual) || 0;
    setSplitMethods((prev) => {
      const nonCashTotal = prev.reduce((sum, m) => {
        if (m.id !== 'cash') {
          return sum + (parseInt(m.nominal) || 0);
        }
        return sum;
      }, 0);

      // Hitung sisa untuk cash (jika belum ada non-cash terisi, cash = full currentPrice)
      const remainingForCash = currentPrice - nonCashTotal;
      const cashVal = remainingForCash > 0 ? remainingForCash : (remainingForCash === 0 && nonCashTotal > 0 ? 0 : currentPrice);

      return prev.map((m) => {
        if (m.id === 'cash') {
          return { ...m, nominal: cashVal > 0 ? cashVal : (currentPrice > 0 ? 0 : '') };
        }
        return m;
      });
    });
    setPaymentMode('split');
  }, [harga_jual]);

  // Beralih ke Mode Single
  const switchToSingle = useCallback((methodId) => {
    const target = methodId || singleMethod || 'cash';
    setSingleMethod(target);
    setPaymentMode('single');
  }, [singleMethod]);

  // Handler Split: Setiap ada perubahan (nambah/kurang) pada non-cash (Transfer/QRIS/EDC),
  // otomatis sesuaikan nominal CASH sebagai penyeimbang
  const handleSplitChange = (methodId, field, value) => {
    const targetPrice = parseInt(harga_jual) || 0;

    if (field === 'nominal' && methodId !== 'cash') {
      const parsedVal = (value === '' || value === undefined) ? 0 : (parseInt(value) || 0);

      setSplitMethods((prev) => {
        // Total non-cash selain method yang sedang diedit
        const otherNonCashTotal = prev.reduce((sum, p) => {
          if (p.id !== 'cash' && p.id !== methodId) {
            return sum + (parseInt(p.nominal) || 0);
          }
          return sum;
        }, 0);

        const totalNonCash = otherNonCashTotal + parsedVal;
        const remainingForCash = targetPrice - totalNonCash;
        const newCash = remainingForCash > 0 ? remainingForCash : 0;

        return prev.map((p) => {
          if (p.id === methodId) {
            return { ...p, nominal: value };
          }
          if (p.id === 'cash') {
            return {
              ...p,
              nominal: newCash > 0 ? newCash : (totalNonCash >= targetPrice ? 0 : '')
            };
          }
          return p;
        });
      });
    } else {
      // Jika user mengedit input CASH secara manual atau mengganti nama_bank
      setSplitMethods((prev) =>
        prev.map((p) => (p.id === methodId ? { ...p, [field]: value } : p))
      );
    }
  };

  const handleFillRemaining = (methodId) => {
    const targetPrice = parseInt(harga_jual) || 0;

    if (methodId !== 'cash') {
      const otherNonCashTotal = splitMethods.reduce((sum, p) => {
        if (p.id !== 'cash' && p.id !== methodId) {
          return sum + (parseInt(p.nominal) || 0);
        }
        return sum;
      }, 0);

      const remaining = targetPrice - otherNonCashTotal;
      const newNominal = remaining > 0 ? remaining : 0;

      setSplitMethods((prev) =>
        prev.map((p) => {
          if (p.id === methodId) {
            const bank = (p.id === 'transfer' || p.id === 'edc') ? (p.nama_bank || defaultBank) : p.nama_bank;
            return { ...p, nominal: newNominal, nama_bank: bank };
          }
          if (p.id === 'cash') {
            return { ...p, nominal: 0 };
          }
          return p;
        })
      );
    } else {
      const nonCashTotal = splitMethods.reduce((sum, p) => {
        return p.id !== 'cash' ? sum + (parseInt(p.nominal) || 0) : sum;
      }, 0);
      const remaining = targetPrice - nonCashTotal;
      const newCash = remaining > 0 ? remaining : 0;

      setSplitMethods((prev) =>
        prev.map((p) => (p.id === 'cash' ? { ...p, nominal: newCash } : p))
      );
    }
  };

  const handleResetSplit = (methodId) => {
    const targetPrice = parseInt(harga_jual) || 0;

    if (methodId !== 'cash') {
      setSplitMethods((prev) => {
        const remainingNonCashTotal = prev.reduce((sum, p) => {
          if (p.id !== 'cash' && p.id !== methodId) {
            return sum + (parseInt(p.nominal) || 0);
          }
          return sum;
        }, 0);

        const remainingForCash = targetPrice - remainingNonCashTotal;
        const newCash = remainingForCash > 0 ? remainingForCash : 0;

        return prev.map((p) => {
          if (p.id === methodId) {
            return { ...p, nominal: '' };
          }
          if (p.id === 'cash') {
            return {
              ...p,
              nominal: newCash > 0 ? newCash : (remainingNonCashTotal >= targetPrice ? 0 : '')
            };
          }
          return p;
        });
      });
    } else {
      setSplitMethods((prev) =>
        prev.map((p) => (p.id === 'cash' ? { ...p, nominal: '' } : p))
      );
    }
  };

  // Shortcut Keyboard: Alt+1 (Cash), Alt+2 (QRIS), Alt+3 (Transfer), Alt+4 (EDC), Alt+S (Toggle Split)
  useEffect(() => {
    const handleKeyShortcut = (e) => {
      if (e.altKey) {
        if (e.key === '1') {
          e.preventDefault();
          switchToSingle('cash');
        } else if (e.key === '2') {
          e.preventDefault();
          switchToSingle('qris');
        } else if (e.key === '3') {
          e.preventDefault();
          switchToSingle('transfer');
        } else if (e.key === '4') {
          e.preventDefault();
          switchToSingle('edc');
        } else if (e.key?.toLowerCase() === 's') {
          e.preventDefault();
          setPaymentMode((prev) => (prev === 'single' ? 'split' : 'single'));
        }
      }
    };
    window.addEventListener('keydown', handleKeyShortcut);
    return () => window.removeEventListener('keydown', handleKeyShortcut);
  }, [switchToSingle]);

  // Kalkulasi Status Pembayaran
  const nominalHargaJual = parseInt(harga_jual) || 0;
  const totalSplit = splitMethods.reduce((sum, p) => sum + (parseInt(p.nominal) || 0), 0);
  const sisaSplit = nominalHargaJual - totalSplit;

  // Active Payments yang akan dikirim ke backend
  const activePayments = paymentMode === 'single'
    ? [
        {
          metode_pembayaran: singleMethod,
          nama_bank: (singleMethod === 'transfer' || singleMethod === 'edc') ? singleBank : '',
          nominal: nominalHargaJual,
        },
      ]
    : splitMethods
        .filter((p) => (parseInt(p.nominal) || 0) > 0)
        .map((p) => ({
          metode_pembayaran: p.id,
          nama_bank: (p.id === 'transfer' || p.id === 'edc') ? (p.nama_bank || defaultBank) : '',
          nominal: parseInt(p.nominal) || 0,
        }));

  const totalPembayaranSaatIni = activePayments.reduce((sum, p) => sum + (parseInt(p.nominal) || 0), 0);

  // Buka Modal Konfirmasi Sebelum Submit
  const handlePreSubmit = (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!hasValidItem) {
      toast.warning('Pilih / scan barang terlebih dahulu');
      return;
    }
    if (nominalHargaJual <= 0) {
      toast.warning('Masukkan nominal harga penjualan terlebih dahulu');
      return;
    }

    setIsConfirmOpen(true);
  };

  // Eksekusi Submit Final dari Modal Konfirmasi
  const handleConfirmSubmit = async () => {
    if (nominalHargaJual <= 0 || !hasValidItem) return;

    if (totalPembayaranSaatIni !== nominalHargaJual) {
      toast.error(
        `Total pembayaran (Rp ${numberWithCommas(totalPembayaranSaatIni)}) belum sesuai dengan harga jual (Rp ${numberWithCommas(nominalHargaJual)})`,
        { autoClose: 3000 }
      );
      return;
    }

    if (paymentMode === 'split' && sisaSplit !== 0) {
      toast.error(
        `Total split (Rp ${numberWithCommas(totalSplit)}) belum sesuai dengan total harga (Rp ${numberWithCommas(nominalHargaJual)})`,
        { autoClose: 3000 }
      );
      return;
    }

    if (paymentMode === 'single' && (singleMethod === 'transfer' || singleMethod === 'edc') && !singleBank) {
      toast.error('Pilih rekening / mesin bank terlebih dahulu');
      return;
    }

    setBtnLoading(true);

    const data = {
      harga_jual: nominalHargaJual,
      item_id,
      gambar,
      payments: activePayments,
      nama_pembeli: namaPembeli.trim() || null,
      no_hp: noHp.trim() || null,
    };

    try {
      const res = await http.post('/penjualan', data, {
        headers: { Authorization: getAuthorizationHeader() },
      });
      setIsConfirmOpen(false);
      setIsOpen(true);
      playSound();
      setDataPrint(res.data.data);
      setNamaPembeli('');
      setNoHp('');
      return res.data;
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Data tidak ditemukan', { autoClose: 600 });
      } else if (error.response?.status === 422) {
        toast.error('Input Harga tidak boleh kosong', { autoClose: 600 });
      } else if (error.response?.status === 409) {
        toast.error('Silahkan scan qrcode', { autoClose: 600 });
      } else if (error.response?.status === 400) {
        toast.error(error.response.data?.message || 'Kesalahan validasi', { autoClose: 1500 });
      } else {
        toast.error('Terjadi kesalahan pada server');
      }
    } finally {
      setBtnLoading(false);
    }
  };

  const handleImageChange = (event) => {
    const selectedFiles = event.target.files;
    const selectedFilesArray = Array.from(selectedFiles);

    if (selectImage.length < 1) {
      const reader1 = new FileReader();
      reader1.addEventListener('load', function () {
        setGambar(reader1.result.split(',')[1]);
      });
      reader1.readAsDataURL(selectedFiles[0]);
    }
    const imagesArray = selectedFilesArray.map((file) => {
      return URL.createObjectURL(file);
    });
    setSelectImage(imagesArray);
    event.target.value = '';
  };

  const toko = localStorage.getItem('name');

  function deleteHandler(image) {
    setSelectImage(selectImage.filter((e) => e !== image));
    setGambar('');
    URL.revokeObjectURL(image);
  }

  const tipe_surat = localStorage.getItem('tipe_surat');
  const src = (activeItem?.gambar1 && activeItem.gambar1.length > 0) ? `${imageBaseUrl}${activeItem.gambar1[0]?.asli}` : '';

  return (
    <>

      <div className="w-full mt-2 pb-20">
        <div className="bg-white dark:bg-[#131b2e] shadow-sm rounded-md p-4 sm:p-5 border border-slate-200 dark:border-slate-800">
          <h1 className='uppercase font-bold text-lg pb-1 border-b border-slate-200 dark:border-slate-800 text-slate-800 dark:text-slate-100'>Penjualan {toko}</h1>
          <form onSubmit={handlePreSubmit} className="rounded-md p-1">
            <div>
              {msg && (
                <div className="text-xl flex items-center justify-center font-semibold text-gray-700 dark:text-slate-300">{msg}</div>
              )}
            </div>
            <div className="w-0 h-0 overflow-hidden print:w-full print:h-auto print:overflow-visible">
              {tipe_surat === '1' ? (
                <div ref={strukComponent} className="fixed -left-[9999px] -top-[9999px] w-0 h-0 overflow-hidden invisible pointer-events-none print:visible print:static print:left-auto print:top-auto print:h-screen print:w-full print:overflow-visible print:pointer-events-auto bg-white">
                  <PrintSuratTrxPenjualan isOpen={isOpen} detailPenjualan={dataPrint} />
                </div>
              ) : tipe_surat === '2' ? (
                <div ref={strukComponent} className="fixed -left-[9999px] -top-[9999px] w-0 h-0 overflow-hidden invisible pointer-events-none print:visible print:static print:left-auto print:top-auto print:h-screen print:w-full print:overflow-visible print:pointer-events-auto bg-white">
                  <TestingPrint isOpen={isOpen} dataPrint={dataPrint} detailPenjualan={dataPrint} isTertunda={isTertunda} />
                </div>
              ) : tipe_surat === '3' ? (
                <div ref={strukComponent} className="fixed -left-[9999px] -top-[9999px] w-0 h-0 overflow-hidden invisible pointer-events-none print:visible print:static print:left-auto print:top-auto print:h-screen print:w-full print:overflow-visible print:pointer-events-auto bg-white">
                  <SuratKeruak isOpen={isOpen} detailPenjualan={dataPrint} />
                </div>
              ) : tipe_surat === '4' ? (
                <div ref={strukComponent} className="fixed -left-[9999px] -top-[9999px] w-0 h-0 overflow-hidden invisible pointer-events-none print:visible print:static print:left-auto print:top-auto print:h-screen print:w-full print:overflow-visible print:pointer-events-auto bg-white">
                  <SuratToko isOpen={isOpen} detailPenjualan={dataPrint} />
                </div>
              ) : null}
            </div>
              <div className="w-full ">
                <div className="font-medium">
                  <center><div className="items-center p-2 mb-1 border-b dark:border-slate-800">
                    {!src ? '' :
                      <PhotoProvider>
                        <PhotoView src={src}>
                          <img className="items-center rounded-lg cursor-zoom-in" width={'250px'} src={src}
                            height={'100px'} alt="" />
                        </PhotoView>
                      </PhotoProvider>
                    }
                  </div></center>

                  <div className="flex justify-between items-center p-1 mb-1 border-b dark:border-slate-800">
                    <div className='font-bold dark:text-slate-300'>Kode Barang :</div>
                    <div className='font-bold text-amber-600 dark:text-amber-400'>{loading ? 'memuat data...' : <>{activeItem?.kode_item || '-'}</>}</div>
                  </div>
                  <div className="flex justify-between items-center p-1 mb-1 border-b dark:border-slate-800">
                    <div className='font-bold dark:text-slate-300'>Nama Barang :</div>
                    <div className='font-bold text-amber-600 dark:text-amber-400'>{loading ? 'memuat data...' : <>{activeItem?.jenis_kategori?.nama_jenis || '-'}</>}</div>
                  </div>
                  <div className="flex justify-between items-center p-1 mb-1 border-b dark:border-slate-800">
                    <div className='font-bold dark:text-slate-300'>Berat :</div>
                    <div className='font-bold text-amber-600 dark:text-amber-400'>{loading ? 'memuat data...' : <>{activeItem?.berat ? `${checkBerat(activeItem.berat)} gram` : '-'}</>}</div>
                  </div>
                  <div className="flex justify-between items-center p-1 mb-1 border-b dark:border-slate-800">
                    <div className='font-bold dark:text-slate-300'>Kadar :</div>
                    <div className='font-bold text-amber-600 dark:text-amber-400'>{loading ? 'memuat data...' : <>{activeItem?.karat ? `${activeItem.karat} karat` : '-'}</>}</div>
                  </div>
                  <div className="flex justify-between items-center p-1 mb-1 border-b dark:border-slate-800">
                    <div className='font-bold dark:text-slate-300'>Total Harga :</div>
                    <div className="flex flex-col items-end">
                      <CurrencyInput
                        ref={hargaTotalRef}
                        id="input-harga-total"
                        value={hargaPenjualan}
                        onChange={handleChange}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handlePreSubmit(e);
                          }
                        }}
                        placeholder="Rp 0"
                        disabled={!hasValidItem}
                        className={`w-48 sm:w-56 h-8 px-2 py-0.5 text-base font-bold text-right text-amber-600 dark:text-amber-400 bg-white dark:bg-slate-800 border border-amber-400 dark:border-amber-500/50 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500 placeholder:text-slate-400 dark:placeholder:text-slate-500 transition-all ${
                          !hasValidItem ? 'opacity-60 cursor-not-allowed bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-500' : ''
                        }`}
                      />
                    </div>
                  </div>
                  <div className="flex justify-between items-center p-1 mb-1 border-b dark:border-slate-800">
                    <div className='font-bold text-lg dark:text-slate-300'>Rata-rata Harga :</div>
                    <div className='font-bold text-lg text-amber-600 dark:text-amber-400'>
                        Rp. {numberWithCommas(activeItem?.berat && nominalHargaJual ? Math.round(nominalHargaJual / activeItem.berat) : 0)}
                    </div>
                  </div>
                </div>
              </div>
              <CetakStroke strukComponent={strukComponent} closeModal={closeModal} isOpen={isOpen} />

              {/* Modal Konfirmasi Final Sebelum Simpan Penjualan */}
              <ModalKonfirmasiPenjualan
                isOpen={isConfirmOpen}
                onClose={() => {
                  setIsConfirmOpen(false);
                  setTimeout(() => {
                    hargaTotalRef.current?.focus();
                  }, 100);
                }}
                onConfirm={handleConfirmSubmit}
                isSubmitting={btnLoading}
                item={activeItem}
                nominalHargaJual={nominalHargaJual}
                paymentMode={paymentMode}
                singleMethod={singleMethod}
                singleBank={singleBank}
                setSingleBank={setSingleBank}
                switchToSingle={switchToSingle}
                switchToSplit={switchToSplit}
                splitMethods={splitMethods}
                handleSplitChange={handleSplitChange}
                handleFillRemaining={handleFillRemaining}
                handleResetSplit={handleResetSplit}
                bankList={getEffectiveBankList(singleBank)}
                defaultBank={defaultBank}
                activePayments={activePayments}
                sisaSplit={sisaSplit}
                totalSplit={totalSplit}
                src={src}
                namaPembeli={namaPembeli}
                setNamaPembeli={setNamaPembeli}
                noHp={noHp}
                setNoHp={setNoHp}
              />

              {/* ================= TOMBOL SIMPAN PENJUALAN ================= */}
              <div className="mt-5 flex justify-end">
                <button
                  type="submit"
                  disabled={!hasValidItem || nominalHargaJual <= 0 || btnLoading}
                  className={`px-5 py-2.5 rounded-xl font-bold text-sm transition flex items-center gap-2 ${
                    !hasValidItem || nominalHargaJual <= 0 || btnLoading
                      ? 'bg-slate-200 dark:bg-slate-800 text-slate-400 dark:text-slate-600 cursor-not-allowed'
                      : 'bg-emerald-600 hover:bg-emerald-700 text-white cursor-pointer shadow-md shadow-emerald-600/20 active:scale-98'
                  }`}
                >
                  {btnLoading ? 'Memproses...' : 'Lanjut ke Pembayaran (Enter)'}
                </button>
              </div>
          </form>
        </div>
      </div>
    </>
  );
}
